import Entypo from '@expo/vector-icons/Entypo';
import React, { useEffect, useState } from 'react';
import { RefCallBack } from 'react-hook-form';
import {
  KeyboardTypeOptions,
  Platform,
  StyleSheet,
  TextInput,
  TextInputProps,
  TouchableOpacity,
  View
} from 'react-native';
import CurrencyInput from 'react-native-currency-input';

import { useColor } from '@/hooks/useColor';

import InfoButton from '../buttons/InfoButton';
import Text from '../Text';

export type TextFieldType = 'password' | 'email-address' | 'numeric' | 'numeric-string' | 'default';

export type TextInputBaseProps = {
  refCallback?: RefCallBack | undefined;
  value: string;
  placeholder: string;
  error?: string;
  currency?: boolean;
  type?: TextFieldType;
  hideError?: boolean;
  leadingIcon?: React.ReactNode;
  traillingIcon?: React.ReactNode;
  variant?: 'default' | 'rounded_with_shadow' | 'small_border_radius' | 'gray_borders' | 'chat';
  label?: string;
  autoCapitalize?: TextInputProps['autoCapitalize'];
  maxLength?: TextInputProps['maxLength'];
  multiline?: TextInputProps['multiline'];
  minHeight?: number;
  maxHeight?: number;
  onBlur?: TextInputProps['onBlur'];
  onEndEditing?: TextInputProps['onEndEditing'];
  onSubmitEditing?: TextInputProps['onSubmitEditing'];
  onKeyPress?: TextInputProps['onKeyPress'];
  returnKeyLabel?: TextInputProps['returnKeyLabel'];
  returnKeyType?: TextInputProps['returnKeyType'];
  textContentType?: TextInputProps['textContentType'];
  editable?: TextInputProps['editable'];
  onChangeCallback?: (data: string) => void;
  infoToolTip?: string;
  infoSubLabel?: string;
};

const TextInputBase = ({
  refCallback,
  value,
  placeholder,
  error,
  currency = false,
  type = 'default',
  leadingIcon,
  traillingIcon,
  hideError = false,
  variant = 'default',
  label,
  autoCapitalize = type === 'email-address' ? 'none' : 'sentences',
  multiline,
  minHeight,
  maxHeight,
  maxLength,
  onBlur,
  onEndEditing,
  onSubmitEditing,
  onKeyPress,
  returnKeyLabel,
  returnKeyType,
  textContentType,
  editable = true,
  onChangeCallback,
  infoToolTip,
  infoSubLabel
}: TextInputBaseProps) => {
  const colors = useColor();

  const [secureText, setSecureText] = useState(false);
  const [numValue, setNumValue] = useState<number | null>(null);
  const [computedHeight, setComputedHeight] = useState<number>(
    minHeight ?? getInputStyle(variant, multiline ?? false).height
  );

  useEffect(() => {
    if (type === 'numeric' && value !== null && value !== undefined) {
      if (value?.length > 0) setNumValue(+value);
      else if (typeof value === 'number') setNumValue(value);
      else setNumValue(null);
    }

    if (type === 'password') setSecureText(true);
  }, [type, value]);

  useEffect(() => {
    if (
      !multiline &&
      !minHeight &&
      !maxHeight &&
      minHeight === maxHeight &&
      (!value || value?.length === 0 || value === undefined || value === null)
    ) {
      setComputedHeight(minHeight ?? getInputStyle(variant, multiline ?? false).height);
      return;
    }

    // ToDO: Improve this calculus to take container width into account and fontsize
    // 150 for web and 35 chars per line for mobile
    const charsPerLine = Platform.OS === 'web' ? 150 : 35;
    const baseLineHeight = 30;

    const newLines = (value?.toString()?.match(/\n/g) || [])?.length;
    const roughLines = Math.floor((value?.length ?? 0) / charsPerLine);
    const totalLines = 1 + newLines + roughLines;

    const newHeight = Math.min(
      Math.max(minHeight ?? getInputStyle(variant, multiline ?? false).height, totalLines * baseLineHeight),
      maxHeight ?? getInputStyle(variant, multiline ?? false).height
    );

    setComputedHeight(newHeight);
  }, [value, multiline, minHeight, maxHeight]);

  return (
    <View>
      {label && (
        <View style={[styles.labelContainer, { marginBottom: infoSubLabel ? 5 : 10 }]}>
          <Text fontWeight="700" style={styles.label}>
            {label}
          </Text>
          {infoToolTip && <InfoButton title={label} message={infoToolTip} type="modal" />}
        </View>
      )}
      {infoSubLabel && <Text style={styles.infoSubLabel}>{infoSubLabel}</Text>}

      <View
        style={[
          getInputStyle(variant, multiline ?? false),
          { justifyContent: multiline ? 'flex-start' : 'center', ...(multiline && { height: computedHeight }) }
        ]}>
        <View
          style={[
            styles.inputContainer,
            Platform.OS !== 'web'
              ? {
                  paddingLeft: leadingIcon ? 30 : 0,
                  paddingRight: traillingIcon ? 20 : 0,
                  paddingVertical: variant === 'rounded_with_shadow' && multiline ? 10 : 0
                }
              : {
                  paddingLeft: leadingIcon ? 30 : 0,
                  paddingRight: traillingIcon ? 20 : 0,
                  paddingVertical: variant === 'rounded_with_shadow' && multiline ? 10 : 0,
                  flexDirection: 'row',
                  columnGap: 8
                }
          ]}>
          {leadingIcon && <View style={[styles.iconContainer, styles.leftIcon]}>{leadingIcon}</View>}

          {currency ? (
            <CurrencyInput
              prefix={'$'}
              placeholder={placeholder}
              placeholderTextColor={colors.placeholderText}
              delimiter={','}
              precision={0}
              maxLength={maxLength}
              value={numValue}
              onChangeValue={(number) => {
                onChangeCallback && onChangeCallback(number?.toString() ?? '');
              }}
              onBlur={onBlur}
              allowFontScaling={false}
              returnKeyLabel={'done'}
              returnKeyType={'done'}
              multiline={multiline}
              onEndEditing={onEndEditing}
              onSubmitEditing={onSubmitEditing}
              onKeyPress={onKeyPress}
              secureTextEntry={secureText}
              renderTextInput={(props) => (
                <TextInput
                  {...props}
                  ref={refCallback}
                  style={[
                    styles.inputContainer,
                    {
                      verticalAlign: multiline ? 'top' : 'middle',
                      textAlignVertical: multiline ? 'top' : 'middle'
                    },
                    Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)
                  ]}
                />
              )}
            />
          ) : (
            <TextInput
              ref={refCallback}
              style={[
                styles.inputContainer,
                {
                  verticalAlign: multiline ? 'top' : 'middle',
                  textAlignVertical: multiline ? 'top' : 'middle',
                  maxHeight: variant === 'rounded_with_shadow' && multiline ? 100 : null
                },
                Platform.OS === 'web' && ({ flex: 1, fontSize: 16, outlineStyle: 'none' } as any)
              ]}
              keyboardType={
                type === 'numeric-string' ? 'numeric' : type === 'password' ? 'default' : (type as KeyboardTypeOptions)
              }
              secureTextEntry={type === 'password' ? secureText : false}
              value={type === 'numeric' ? (numValue !== null ? numValue.toString() : '') : (value ?? '')}
              onChangeText={(value) => {
                if (type === 'numeric' || type === 'numeric-string') {
                  const isValid = value === '' || /^\d*\.?\d*$/.test(value) || /^\d*$/.test(value);
                  if (isValid) {
                    onChangeCallback && onChangeCallback(value ?? '');
                  }
                } else {
                  onChangeCallback && onChangeCallback(value ?? '');
                }
              }}
              onBlur={onBlur}
              placeholder={placeholder}
              placeholderTextColor={colors.placeholderText}
              autoCapitalize={autoCapitalize}
              maxLength={maxLength}
              multiline={multiline}
              onEndEditing={onEndEditing}
              onSubmitEditing={onSubmitEditing}
              onKeyPress={onKeyPress}
              returnKeyLabel={returnKeyLabel}
              returnKeyType={returnKeyType}
              textContentType={textContentType}
              allowFontScaling={false}
              editable={editable}
            />
          )}

          {type === 'password' ? (
            <TouchableOpacity
              style={[styles.iconContainer, styles.rightIcon]}
              onPress={() => setSecureText(!secureText)}>
              {secureText ? <Entypo name="eye" size={20} /> : <Entypo name="eye-with-line" size={20} />}
            </TouchableOpacity>
          ) : (
            <View style={[styles.iconContainer, styles.rightIcon]}>{traillingIcon}</View>
          )}
        </View>
      </View>

      {error && !hideError && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

const getInputStyle = (variant: string, multiline: boolean) => {
  switch (variant) {
    case 'rounded_with_shadow':
      return { ...styles.roundedWithShadow, borderRadius: multiline ? 15 : 50 };
    case 'small_border_radius':
      return styles.smallBorderRadius;
    case 'gray_borders':
      return styles.grayBorders;
    default:
      return styles.default;
  }
};

const styles = StyleSheet.create({
  default: {
    height: 56,
    padding: 16,
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: 'white'
  },

  roundedWithShadow: {
    justifyContent: 'center',
    height: 38,
    backgroundColor: 'white',
    paddingHorizontal: 16,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5
  },

  smallBorderRadius: {
    height: 38,
    backgroundColor: 'white',
    paddingHorizontal: 16,
    borderRadius: 5,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5
  },

  grayBorders: {
    height: 38,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: 'darkgray',
    backgroundColor: 'white'
  },

  labelContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 2
  },

  label: {
    color: 'black'
  },

  infoSubLabel: {
    color: 'gray',
    fontStyle: 'italic',
    fontSize: 12,
    marginBottom: 10
  },

  inputContainer: {
    width: '100%',
    position: 'relative',
    fontSize: 16,
    fontWeight: 'normal',
    flex: 1,
    padding: 0,
    includeFontPadding: false,
    textAlignVertical: 'center'
  },

  iconContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1
  },

  leftIcon: {
    left: 0
  },

  rightIcon: {
    right: 0
  },

  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  },

  flexOne: {
    flex: 1
  }
});

export default TextInputBase;
